import { useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Select from "react-select";
import { RegisterForEvent, ValidateEventCoupon } from "../../apis/api/EventsApi";
import "./EventRegistrationForm.css";

// React Select custom styles
const selectStyles = {
  control: (b, s) => ({
    ...b,
    borderColor: s.isFocused ? "#586ad9" : "#e2e8f0",
    boxShadow: s.isFocused ? "0 0 0 3px rgba(88,106,217,.12)" : "none",
    borderRadius: "10px",
    minHeight: "42px",
    background: "#fafbff",
    fontSize: ".85rem",
    "&:hover": { borderColor: "#586ad9" },
  }),
  option: (b, s) => ({
    ...b,
    background: s.isSelected ? "#586ad9" : s.isFocused ? "#eef0fd" : "#fff",
    color: s.isSelected ? "#fff" : "#1a202c",
    fontSize: ".85rem",
  }),
  placeholder: (b) => ({ ...b, color: "#b0bec5", fontSize: ".82rem" }),
  singleValue: (b) => ({ ...b, color: "#1a202c", fontSize: ".85rem" }),
};

const errSelectStyles = {
  ...selectStyles,
  control: (b, s) => ({ ...selectStyles.control(b, s), borderColor: s.isFocused ? "#586ad9" : "#ef4444" }),
};

const SALUTATION_OPTS = ["Mr.", "Mrs.", "Ms.", "Dr.", "Prof."].map(v => ({ value: v, label: v }));
const TSHIRT_OPTS     = ["XS", "S", "M", "L", "XL", "XXL", "XXXL"].map(v => ({ value: v, label: v }));
const DIETARY_OPTS    = ["None", "Vegetarian", "Vegan", "Halal", "Kosher", "Gluten-Free", "Other"].map(v => ({ value: v, label: v }));

const emptyAttendee = () => ({ salutation: null, first_name: "", last_name: "", email: "", mobile: "", designation: "" });

const EventRegistrationForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const event    = location.state?.event;
  const prefill  = location.state?.prefill || {};

  const [form, setForm] = useState({
    salutation:       prefill.salutation ? { value: prefill.salutation, label: prefill.salutation } : null,
    first_name:       prefill.first_name || "",
    last_name:        prefill.last_name  || "",
    email:            prefill.email      || "",
    mobile:           prefill.mobile     || "",
    whatsapp:         prefill.mobile     || "",
    designation:      prefill.designation || "",
    badge_name:       "",
    tshirt_size:      null,
    dietary:          { value: "None", label: "None" },
    group_tour:       false,
    spouse_attending: false,
    spouse_name:      "",
    spouse_email:     "",
  });

  const [attendees, setAttendees] = useState([]);
  const [status, setStatus]       = useState(null);
  const [errors, setErrors]       = useState({});
  const [coupon, setCoupon]       = useState({ code: "", applied: false, discount: 0, discount_type: "flat", name: "", id: null, currency_symbol: "$" });
  const [couponStatus, setCouponStatus] = useState(null); // 'validating', 'valid', 'invalid'

  const set = (field, val) => setForm(p => ({ ...p, [field]: val }));

  // Attendee helpers
  const addAttendee    = () => setAttendees(p => [...p, emptyAttendee()]);
  const removeAttendee = (i) => setAttendees(p => p.filter((_, idx) => idx !== i));
  const setAttendee    = (i, field, val) => setAttendees(p => p.map((a, idx) => idx === i ? { ...a, [field]: val } : a));

  const validate = () => {
    const e = {};
    if (!form.salutation)          e.salutation  = "Required";
    if (!form.first_name.trim())   e.first_name  = "Required";
    if (!form.last_name.trim())    e.last_name   = "Required";
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = "Valid email required";
    if (!form.mobile.trim())       e.mobile      = "Required";
    if (!form.whatsapp.trim())     e.whatsapp    = "Required";
    if (!form.tshirt_size)         e.tshirt_size = "Required";
    if (form.spouse_attending && !form.spouse_name.trim()) e.spouse_name = "Required";
    attendees.forEach((a, i) => {
      if (!a.first_name.trim()) e[`att_fn_${i}`] = "Required";
      if (!a.last_name.trim())  e[`att_ln_${i}`] = "Required";
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateCoupon = async () => {
    if (!coupon.code.trim()) {
      setCouponStatus('invalid');
      return;
    }

    setCouponStatus('validating');
    
    try {
      const result = await dispatch(ValidateEventCoupon({
        coupon_code: coupon.code,
        event_id: event?.id
      })).unwrap();

      if (result.success) {
        setCoupon({
          ...coupon,
          applied: true,
          discount: result.discount,
          discount_type: result.discount_type || 'flat',
          name: result.name,
          id: result.coupon_id,
          currency_symbol: result.currency_symbol || '$'
        });
        setCouponStatus('valid');
      } else {
        setCoupon({ ...coupon, applied: false, discount: 0, name: "", id: null, currency_symbol: '$' });
        setCouponStatus('invalid');
      }
    } catch (error) {
      console.error('Coupon validation error:', error);
      setCoupon({ ...coupon, applied: false, discount: 0, name: "", id: null, currency_symbol: '$' });
      setCouponStatus('invalid');
    }
  };

  const removeCoupon = () => {
    setCoupon({ code: "", applied: false, discount: 0, name: "", id: null, currency_symbol: "$" });
    setCouponStatus(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus("loading");
    try {
      const payload = {
        event_id:         event?.id,
        salutation:       form.salutation?.value,
        first_name:       form.first_name,
        last_name:        form.last_name,
        email:            form.email,
        mobile:           form.mobile,
        whatsapp:         form.whatsapp,
        designation:      form.designation,
        badge_name:       form.badge_name,
        tshirt_size:      form.tshirt_size?.value,
        dietary:          form.dietary?.value,
        group_tour:       form.group_tour,
        spouse_attending: form.spouse_attending,
        spouse_name:      form.spouse_name,
        spouse_email:     form.spouse_email,
        attendees:        attendees.map(a => ({ ...a, salutation: a.salutation?.value })),
        coupon_id:        coupon.applied ? coupon.id : null,
        coupon_code:      coupon.applied ? coupon.code : null,
        discount_applied: coupon.applied 
          ? (coupon.discount_type === 'percentage' 
              ? Math.min((parseFloat(event?.amount || 0) * parseFloat(coupon.discount)) / 100, parseFloat(event?.amount || 0))
              : Math.min(parseFloat(coupon.discount), parseFloat(event?.amount || 0)))
          : 0
      };
      const res = await dispatch(RegisterForEvent(payload)).unwrap();
      const s = res?.data?.status;
      setStatus(s === "already" ? "already" : "success");
    } catch {
      setStatus("error");
    }
  };

  if (!event) return (
    <div className="erf-empty">
      <i className="bx bx-error-circle"></i>
      <p>No event selected.</p>
      <button onClick={() => navigate(-1)}>Go Back</button>
    </div>
  );

  if (status === "success") return (
    <div className="erf-result success">
      <div className="erf-result-icon"><i className="bx bx-check-circle"></i></div>
      <h2>Registration Submitted!</h2>
      <p>Your registration for <strong>{event.Event_Name}</strong> has been submitted.</p>
      <p className="erf-result-sub">You will be notified once the admin approves your registration.</p>
      <button className="erf-back-btn" onClick={() => navigate("/EventsMenu")}>
        <i className="bx bx-arrow-back"></i> Back to Events
      </button>
    </div>
  );

  if (status === "already") return (
    <div className="erf-result already">
      <div className="erf-result-icon"><i className="bx bx-info-circle"></i></div>
      <h2>Already Registered</h2>
      <p>You have already registered for <strong>{event.Event_Name}</strong>.</p>
      <button className="erf-back-btn" onClick={() => navigate("/EventsMenu")}>
        <i className="bx bx-arrow-back"></i> Back to Events
      </button>
    </div>
  );

  return (
    <div className="erf-wrap">
      {/* Banner */}
      <div className="erf-banner">
        {event.file_url && <img src={event.file_url.replace("/thumb", "")} alt={event.Event_Name} className="erf-banner-img"/>}
        <div className="erf-banner-overlay">
          <span className="erf-banner-badge"><i className="bx bx-calendar-event"></i> Event Registration</span>
          <h1 className="erf-banner-title">{event.Event_Name}</h1>
          {event.Event_Title && <p className="erf-banner-sub">{event.Event_Title}</p>}
        </div>
      </div>

      <form className="erf-form" onSubmit={handleSubmit} noValidate>

        {/* ── Delegate Info ── */}
        <div className="erf-section">
          <div className="erf-section-header">
            <div className="erf-section-icon"><i className="bx bx-user"></i></div>
            <div>
              <h3 className="erf-section-title">Delegate Information</h3>
              <p className="erf-section-sub">Your personal details as they should appear on your badge.</p>
            </div>
          </div>

          <div className="erf-grid">
            <div className="erf-field">
              <label className="erf-label">Salutation <span className="req">*</span></label>
              <Select options={SALUTATION_OPTS} value={form.salutation}
                onChange={v => set("salutation", v)} placeholder="Select salutation"
                styles={errors.salutation ? errSelectStyles : selectStyles} isClearable/>
              {errors.salutation && <span className="erf-err">{errors.salutation}</span>}
            </div>

            <div className="erf-field">
              <label className="erf-label">First Name <span className="req">*</span></label>
              <input className={`erf-input ${errors.first_name ? "err" : ""}`} placeholder="First name"
                value={form.first_name} onChange={e => set("first_name", e.target.value)}/>
              {errors.first_name && <span className="erf-err">{errors.first_name}</span>}
            </div>

            <div className="erf-field erf-col-2">
              <label className="erf-label">Last Name <span className="req">*</span></label>
              <input className={`erf-input ${errors.last_name ? "err" : ""}`} placeholder="Last name"
                value={form.last_name} onChange={e => set("last_name", e.target.value)}/>
              {errors.last_name && <span className="erf-err">{errors.last_name}</span>}
            </div>

            <div className="erf-field erf-col-2">
              <label className="erf-label">Email Address <span className="req">*</span></label>
              <input type="email" className={`erf-input ${errors.email ? "err" : ""}`} placeholder="delegate@company.com"
                value={form.email} onChange={e => set("email", e.target.value)}/>
              {errors.email && <span className="erf-err">{errors.email}</span>}
            </div>

            <div className="erf-field erf-col-2">
              <label className="erf-label">Mobile Number <span className="req">*</span></label>
              <input className={`erf-input ${errors.mobile ? "err" : ""}`} placeholder="+1 234 567 8900"
                value={form.mobile} onChange={e => set("mobile", e.target.value)}/>
              {errors.mobile && <span className="erf-err">{errors.mobile}</span>}
            </div>

            <div className="erf-field erf-col-2">
              <label className="erf-label">WhatsApp Number <span className="req">*</span></label>
              <input className={`erf-input ${errors.whatsapp ? "err" : ""}`} placeholder="+1 234 567 8900"
                value={form.whatsapp} onChange={e => set("whatsapp", e.target.value)}/>
              {errors.whatsapp && <span className="erf-err">{errors.whatsapp}</span>}
            </div>

            <div className="erf-field erf-col-2">
              <label className="erf-label">Designation / Job Title</label>
              <input className="erf-input" placeholder="e.g. Director, Manager"
                value={form.designation} onChange={e => set("designation", e.target.value)}/>
            </div>

            <div className="erf-field erf-col-2">
              <label className="erf-label">Name on Badge <span className="erf-hint">(if different)</span></label>
              <input className="erf-input" placeholder="As it should appear on badge"
                value={form.badge_name} onChange={e => set("badge_name", e.target.value)}/>
            </div>

            <div className="erf-field erf-col-2">
              <label className="erf-label">T-Shirt Size <span className="req">*</span></label>
              <Select options={TSHIRT_OPTS} value={form.tshirt_size}
                onChange={v => set("tshirt_size", v)} placeholder="Select size"
                styles={errors.tshirt_size ? errSelectStyles : selectStyles}/>
              {errors.tshirt_size && <span className="erf-err">{errors.tshirt_size}</span>}
            </div>

            <div className="erf-field erf-col-2">
              <label className="erf-label">Dietary Restrictions</label>
              <Select options={DIETARY_OPTS} value={form.dietary}
                onChange={v => set("dietary", v)} placeholder="Select..."
                styles={selectStyles}/>
            </div>
          </div>

          {/* Group tour */}
          <div className="erf-radio-group">
            <p className="erf-radio-label">Are you interested in joining a group tour?</p>
            <div className="erf-radio-opts">
              {[{ v: true, l: "Yes" }, { v: false, l: "No" }].map(o => (
                <label key={o.l} className={`erf-radio-opt ${form.group_tour === o.v ? "active" : ""}`}
                  onClick={() => set("group_tour", o.v)}>
                  <span className={`erf-radio-dot ${form.group_tour === o.v ? "active" : ""}`}></span> {o.l}
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* ── Additional Attendees ── */}
        <div className="erf-section">
          <div className="erf-section-header">
            <div className="erf-section-icon attendee"><i className="bx bx-user-plus"></i></div>
            <div>
              <h3 className="erf-section-title">Additional Attendees</h3>
              <p className="erf-section-sub">Add guests who will attend alongside you.</p>
            </div>
            <button type="button" className="erf-add-btn" onClick={addAttendee}>
              <i className="bx bx-plus"></i> Add Attendee
            </button>
          </div>

          {attendees.length === 0 && (
            <div className="erf-empty-attendees">
              <i className="bx bx-group"></i>
              <p>No additional attendees added yet.</p>
              <button type="button" className="erf-add-btn-outline" onClick={addAttendee}>
                <i className="bx bx-plus"></i> Add Attendee
              </button>
            </div>
          )}

          {attendees.map((a, i) => (
            <div key={i} className="erf-attendee-card">
              <div className="erf-attendee-header">
                <span className="erf-attendee-num">Attendee {i + 1}</span>
                <button type="button" className="erf-remove-btn" onClick={() => removeAttendee(i)}>
                  <i className="bx bx-trash"></i> Remove
                </button>
              </div>
              <div className="erf-grid">
                <div className="erf-field">
                  <label className="erf-label">Salutation</label>
                  <Select options={SALUTATION_OPTS} value={a.salutation}
                    onChange={v => setAttendee(i, "salutation", v)}
                    placeholder="Select" styles={selectStyles} isClearable/>
                </div>
                <div className="erf-field erf-col-2">
                  <label className="erf-label">First Name <span className="req">*</span></label>
                  <input className={`erf-input ${errors[`att_fn_${i}`] ? "err" : ""}`} placeholder="First name"
                    value={a.first_name} onChange={e => setAttendee(i, "first_name", e.target.value)}/>
                  {errors[`att_fn_${i}`] && <span className="erf-err">{errors[`att_fn_${i}`]}</span>}
                </div>
                <div className="erf-field erf-col-2">
                  <label className="erf-label">Last Name <span className="req">*</span></label>
                  <input className={`erf-input ${errors[`att_ln_${i}`] ? "err" : ""}`} placeholder="Last name"
                    value={a.last_name} onChange={e => setAttendee(i, "last_name", e.target.value)}/>
                  {errors[`att_ln_${i}`] && <span className="erf-err">{errors[`att_ln_${i}`]}</span>}
                </div>
                <div className="erf-field erf-col-2">
                  <label className="erf-label">Email</label>
                  <input type="email" className="erf-input" placeholder="email@example.com"
                    value={a.email} onChange={e => setAttendee(i, "email", e.target.value)}/>
                </div>
                <div className="erf-field erf-col-2">
                  <label className="erf-label">Mobile</label>
                  <input className="erf-input" placeholder="+1 234 567 8900"
                    value={a.mobile} onChange={e => setAttendee(i, "mobile", e.target.value)}/>
                </div>
                <div className="erf-field erf-col-2">
                  <label className="erf-label">Designation</label>
                  <input className="erf-input" placeholder="Job title"
                    value={a.designation} onChange={e => setAttendee(i, "designation", e.target.value)}/>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Spouse ── */}
        <div className="erf-section">
          <div className="erf-section-header">
            <div className="erf-section-icon spouse"><i className="bx bx-group"></i></div>
            <div>
              <h3 className="erf-section-title">Spouse / Guest Information</h3>
              <p className="erf-section-sub">Will your spouse or partner be accompanying you?</p>
            </div>
          </div>
          <div className="erf-info-box">
            <i className="bx bx-info-circle"></i>
            <p>Spouse/Partner fee includes social events only. For business participation, register them as a separate delegate.</p>
          </div>
          <div className="erf-radio-group">
            <p className="erf-radio-label">Will your Spouse/Partner be accompanying you?</p>
            <div className="erf-radio-opts">
              {[{ v: true, l: "Yes" }, { v: false, l: "No" }].map(o => (
                <label key={o.l} className={`erf-radio-opt ${form.spouse_attending === o.v ? "active" : ""}`}
                  onClick={() => set("spouse_attending", o.v)}>
                  <span className={`erf-radio-dot ${form.spouse_attending === o.v ? "active" : ""}`}></span> {o.l}
                </label>
              ))}
            </div>
          </div>
          {form.spouse_attending && (
            <div className="erf-grid" style={{ marginTop: "1rem" }}>
              <div className="erf-field erf-col-2">
                <label className="erf-label">Spouse / Partner Full Name <span className="req">*</span></label>
                <input className={`erf-input ${errors.spouse_name ? "err" : ""}`} placeholder="Full name"
                  value={form.spouse_name} onChange={e => set("spouse_name", e.target.value)}/>
                {errors.spouse_name && <span className="erf-err">{errors.spouse_name}</span>}
              </div>
              <div className="erf-field erf-col-2">
                <label className="erf-label">Spouse / Partner Email</label>
                <input type="email" className="erf-input" placeholder="email@example.com"
                  value={form.spouse_email} onChange={e => set("spouse_email", e.target.value)}/>
              </div>
            </div>
          )}
        </div>

        {/* ── Coupon Code — only show if event has a fee ── */}
        {parseFloat(event?.amount || 0) > 0 && (
        <div className="erf-section">
          <div className="erf-section-header">
            <div className="erf-section-icon coupon"><i className="bx bx-gift"></i></div>
            <div>
              <h3 className="erf-section-title">Discount Coupon</h3>
              <p className="erf-section-sub">Have a coupon code? Apply it to get a discount on your registration.</p>
            </div>
          </div>

          {!coupon.applied ? (
            <div className="erf-coupon-input-wrap">
              <div className="erf-coupon-input-group">
                <input 
                  type="text" 
                  className="erf-coupon-input" 
                  placeholder="Enter coupon code"
                  value={coupon.code}
                  onChange={(e) => setCoupon({ ...coupon, code: e.target.value.toUpperCase() })}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), validateCoupon())}
                  disabled={couponStatus === 'validating'}
                />
                <button 
                  type="button" 
                  className="erf-coupon-apply-btn"
                  onClick={validateCoupon}
                  disabled={!coupon.code.trim() || couponStatus === 'validating'}
                >
                  {couponStatus === 'validating' ? (
                    <><i className="bx bx-loader-alt bx-spin"></i> Validating...</>
                  ) : (
                    <><i className="bx bx-check-circle"></i> Apply</>
                  )}
                </button>
              </div>
              {couponStatus === 'invalid' && (
                <div className="erf-coupon-error">
                  <i className="bx bx-error-circle"></i> Invalid or expired coupon code. Please check and try again.
                </div>
              )}
              {!couponStatus && (
                <div className="erf-coupon-hint">
                  <i className="bx bx-info-circle"></i> Enter your promotional code to receive a discount on your registration fee.
                </div>
              )}
            </div>
          ) : (
            <div className="erf-coupon-applied">
              <div className="erf-coupon-applied-content">
                <div className="erf-coupon-applied-icon">
                  <i className="bx bxs-badge-check"></i>
                </div>
                <div className="erf-coupon-applied-info">
                  <div className="erf-coupon-success-badge">
                    <i className="bx bx-party"></i> Coupon Applied!
                  </div>
                  <p className="erf-coupon-applied-name">{coupon.name}</p>
                  <div className="erf-coupon-details">
                    <span className="erf-coupon-code-badge">{coupon.code}</span>
                    <span className="erf-coupon-savings-badge">
                      <i className="bx bx-dollar-circle"></i> 
                      {coupon.discount_type === 'percentage' 
                        ? `${coupon.discount}% off`
                        : `Save ${coupon.currency_symbol}${coupon.discount}`}
                    </span>
                  </div>
                </div>
              </div>
              <button 
                type="button" 
                className="erf-coupon-remove-btn"
                onClick={removeCoupon}
              >
                <i className="bx bx-x"></i>
              </button>
            </div>
          )}
        </div>
        )} {/* end coupon section */}

        {/* ── Price Breakdown — always show ── */}
        <div className="erf-section erf-price-breakdown">
          <div className="erf-section-header">
              <div className="erf-section-icon" style={{background:'linear-gradient(135deg,#10b981,#059669)'}}><i className="bx bx-receipt"></i></div>
              <div>
                <h3 className="erf-section-title">Price Summary</h3>
                <p className="erf-section-sub">Breakdown of your registration fee</p>
              </div>
            </div>

            <div className="erf-breakdown-table">
              <div className="erf-breakdown-row">
                <span className="erf-breakdown-label">Registration Fee</span>
                <span className="erf-breakdown-value">{coupon.currency_symbol || '$'}{parseFloat(event.amount).toFixed(2)}</span>
              </div>

              {coupon.applied && (() => {
                const original = parseFloat(event.amount) || 0;
                const discAmt = coupon.discount_type === 'percentage'
                  ? Math.min((original * parseFloat(coupon.discount)) / 100, original)
                  : Math.min(parseFloat(coupon.discount) || 0, original);
                const final = Math.max(0, original - discAmt);
                return (
                  <>
                    <div className="erf-breakdown-row erf-breakdown-discount">
                      <span className="erf-breakdown-label">
                        <i className="bx bx-tag"></i> Discount
                        {coupon.discount_type === 'percentage'
                          ? ` (${coupon.discount}%)`
                          : ''} — <em>{coupon.code}</em>
                      </span>
                      <span className="erf-breakdown-value erf-discount-value">
                        -{coupon.currency_symbol || '$'}{discAmt.toFixed(2)}
                      </span>
                    </div>
                    <div className="erf-breakdown-divider" />
                    <div className="erf-breakdown-row erf-breakdown-total">
                      <span className="erf-breakdown-label">Total Payable</span>
                      <span className="erf-breakdown-value erf-total-value">
                        {coupon.currency_symbol || '$'}{final.toFixed(2)}
                      </span>
                    </div>
                  </>
                );
              })()}

              {!coupon.applied && (
                <>
                  <div className="erf-breakdown-divider" />
                  <div className="erf-breakdown-row erf-breakdown-total">
                    <span className="erf-breakdown-label">Total Payable</span>
                    <span className="erf-breakdown-value erf-total-value">
                      {coupon.currency_symbol || '$'}{parseFloat(event.amount).toFixed(2)}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

        {/* Submit */}
        <div className="erf-submit-row">
          <button type="button" className="erf-cancel-btn" onClick={() => navigate(-1)}>
            <i className="bx bx-arrow-back"></i> Cancel
          </button>
          <button type="submit" className="erf-submit-btn" disabled={status === "loading"}>
            {status === "loading"
              ? <><i className="bx bx-loader-alt bx-spin"></i> Submitting...</>
              : <><i className="bx bx-send"></i> Submit Registration</>}
          </button>
        </div>
        {status === "error" && (
          <div className="erf-error-msg">
            <i className="bx bx-error-circle"></i> Something went wrong. Please try again.
          </div>
        )}
      </form>
    </div>
  );
};

export default EventRegistrationForm;
